import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';

interface SectionCardProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Compact white card shell shared by every clinical-record section — icon bubble +
 *  title + optional trailing actions, tight padding so several stack without feeling
 *  like a long administrative form. */
export function SectionCard({
  icon,
  title,
  description,
  actions,
  children,
  className,
}: SectionCardProps): JSX.Element {
  const Icon = icon;
  return (
    <Card className={cn('border-border/70', className)}>
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 p-4 pb-3">
        <div className="flex items-center gap-2">
          {Icon ? (
            <span className="bg-primary/10 text-primary flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
              <Icon className="h-4 w-4" aria-hidden />
            </span>
          ) : null}
          <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        </div>
        {actions}
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-0">
        {description ? (
          <p className="text-muted-foreground -mt-1 text-xs">{description}</p>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}
